import { BrowseArtefacts } from '@modules/bookmark/components/BrowseArtefacts';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { Direction } from '@modules/common/types';
import { directionToString } from '@modules/common/utils';
import { ANALYSIS_WIDGET_MIN_HEIGHT } from '@modules/dashboard/constants';
import {
  addCellToDashboardLayout,
  getNewCellID,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import { useUpsertDashboard } from '@modules/dashboard/hooks';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { DialogContent } from '@mui/material';
import {
  AnalysisUseCase,
  ArtefactType,
  DashboardCell,
  DashboardMetadataInput,
  DashboardViewFragment,
} from 'generated/graphql';
import { cloneDeep } from 'lodash';
import pluralize from 'pluralize';

interface AddExistingAnalysesToDashboardDialogContentProps {
  onAddAnalysis?: (cell?: DashboardCell) => void;
  dashboard: DashboardViewFragment;
  rowID?: string;
  cellInsertDirection: Direction;
}

export const AddExistingAnalysesToDashboardDialogContent = ({
  onAddAnalysis,
  dashboard,
  rowID,
  cellInsertDirection,
}: AddExistingAnalysesToDashboardDialogContentProps) => {
  const { closeModal } = useMuiModalContext();
  const { showErrorNotification, showSuccessNotification } =
    useNotificationContext();
  const metadata = useDashboardStore(({ metadata }) => metadata);
  const dashboardTypeDisplayName = useDashboardStore(
    ({ visual }) => visual.dashboardTypeDisplayName,
  );
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const cellMinimumHeightsByRowID = useDashboardStore(
    ({ visual }) => visual.cellMinimumHeightsByRowID,
  );

  const getUpdatedMetadata = (
    newCells: DashboardCell[],
  ): DashboardMetadataInput => {
    if (!newCells.length) {
      return metadata;
    }

    const clonedLayout = cloneDeep(metadata.layout);

    newCells.forEach((cell) => {
      addCellToDashboardLayout({
        cell,
        rowID,
        cellInsertDirection,
        layout: clonedLayout,
      });
    });

    return { layout: clonedLayout };
  };

  const { upsertDashboard, loading } = useUpsertDashboard();

  const dashboardAnalysesIDs =
    dashboard.widgets
      ?.filter(({ analysis }) => !!analysis)
      ?.map(({ id }) => id) ?? [];

  const addAnalyses = async (selectedArtefactIDs: string[]) => {
    try {
      if (!selectedArtefactIDs.length) {
        return;
      }

      const newCells: DashboardCell[] = selectedArtefactIDs.map(
        (artefactID) => ({
          id: getNewCellID(),
          columnSpan: 12,
          contentID: artefactID,
        }),
      );

      const metadataV2 = getUpdatedMetadata(newCells);
      if (metadataV2) {
        dispatch({
          type: DashboardActionType.SET_METADATA,
          payload: metadataV2,
        });
      }

      const updatedRowID =
        rowID ?? metadataV2.layout?.rows[metadataV2.layout?.rows.length - 1].id;
      if (updatedRowID) {
        const cellHeights = newCells.reduce((acc, cell) => {
          acc[cell.id] = ANALYSIS_WIDGET_MIN_HEIGHT;

          return acc;
        }, {} as Record<string, number>);

        dispatch({
          type: DashboardActionType.SET_VISUAL,
          payload: {
            cellMinimumHeightsByRowID: {
              ...cellMinimumHeightsByRowID,
              [updatedRowID]: {
                ...cellMinimumHeightsByRowID[updatedRowID],
                ...cellHeights,
              },
            },
          },
        });
      }

      await upsertDashboard({
        dashboardProps: {
          addAnalysisIDs: selectedArtefactIDs,
          metadataV2,
        },
        shouldRefetchAnalyses: true,
        shouldRefetchDashboard: true,
      });

      closeModal();
      showSuccessNotification({
        contentComponent: `Successfully added ${newCells.length} ${pluralize(
          'item',
          newCells.length,
        )} to the ${dashboardTypeDisplayName}
        `,
        props: {},
      });
      onAddAnalysis?.(newCells[0]);

      trackDashboardEvent({
        eventName: 'Dashboard Add Content',
        placement: directionToString(cellInsertDirection),
        type: 'analysis',
        from: 'Dashboard Page',
      });
      trackDashboardEvent({
        eventName: 'Analysis Added to Dashboard',
        dashboardLink: window.location.href,
        from: 'Dashboard Page',
      });
    } catch {
      showErrorNotification({
        contentComponent: `Failed to add analysis`,
        props: {},
      });
    }
  };

  return (
    <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
      <BrowseArtefacts
        shouldEnableSelection
        title={`Select Saved Items to add to ${dashboardTypeDisplayName}`}
        allowedArtefactTypes={[ArtefactType.Analysis]}
        allowedAnalysisUseCases={[
          AnalysisUseCase.Default,
          AnalysisUseCase.Compare,
          AnalysisUseCase.Feed,
        ]}
        addedArtefactIDs={dashboardAnalysesIDs}
        searchInputPlaceholder={`Search for saved Feed and Quantify by title or creator`}
        onSubmit={addAnalyses}
        submitButtonLabel={`Add to ${dashboardTypeDisplayName}`}
        isSubmitting={loading}
      />
    </DialogContent>
  );
};
